import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster, toast } from "sonner";
import { useState, useEffect, createContext, useContext, Component, useRef, useCallback } from "react";
import axios from "axios";
import { getToken, setToken as storeToken, setStoredUser, clearSession, setLastActivity, getLastActivity } from "./lib/auth";
import { addToOfflineQueue, syncOfflineQueue, getOfflineQueue } from "./lib/offlineQueue";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import PublicPresentationPage from "./pages/PublicPresentationPage";
import PharmacyDashboard from "./pages/PharmacyDashboard";
import ProductsPage from "./pages/ProductsPage";
import ImportCSV from "./pages/ImportCSV";
import DestructionsPage from "./pages/DestructionsPage";
import DestructionExpress from "./pages/DestructionExpress";
import ReportsPage from "./pages/ReportsPage";
import AdminDashboard from "./pages/AdminDashboard";
import NotificationsPage from "./pages/NotificationsPage";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import InternalFinancePage from "./pages/InternalFinancePage";
import TrainingVideosPage from "./pages/TrainingVideosPage";
import PublicVideoViewer from "./pages/PublicVideoViewer";
import LogBankStatementsPage from "./pages/LogBankStatementsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import RecommendationsPage from "./pages/RecommendationsPage";
import SentinelPage from "./pages/SentinelPage";
import PharmExchangePage from "./pages/PharmExchangePage";
import ITPPPage from "./pages/ITPPPage";
import QRCodePage from "./pages/QRCodePage";
import MonthlyDashboardPage from "./pages/MonthlyDashboardPage";
import DownloadsPage from "./pages/DownloadsPage";
import APIPage from "./pages/APIPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import DeploymentGuidePage from "./pages/DeploymentGuidePage";
import SystemStatusPage from "./pages/SystemStatusPage";
import QRLandingPage from "./pages/QRLandingPage";
import PatientReportPage from "./pages/PatientReportPage";
import HubPage from "./pages/HubPage";
import QuickDeclarePage from "./pages/QuickDeclarePage";
import SubscriptionManagementPage from "./pages/SubscriptionManagementPage";
import PrescriptionScannerPage from "./pages/PrescriptionScannerPage";
import ConseilHistoryPage from "./pages/ConseilHistoryPage";
import ProductScannerPage from "./pages/ProductScannerPage";
import StockIntelligencePage from "./pages/StockIntelligencePage";

// CMES-LOG Pages
import LogDashboardPage from "./pages/LogDashboardPage";
import LogInvoicesPage from "./pages/LogInvoicesPage";
import LogInsurersPage from "./pages/LogInsurersPage";
import LogAnalyticsPage from "./pages/LogAnalyticsPage";
import LogDeliveriesPage from "./pages/LogDeliveriesPage";
import LogBordereauxPage from "./pages/LogBordereauxPage";
import LogCoursesPage from "./pages/LogCoursesPage";
import LogArchivesPage from "./pages/LogArchivesPage";
import LogDocumentationPage from "./pages/LogDocumentationPage";
import LogPOSPage from "./pages/LogPOSPage";
import LogNotificationsPage from "./pages/LogNotificationsPage";
import LogMonthlyReportPage from "./pages/LogMonthlyReportPage";
import TonnageAnalyticsPage from "./pages/TonnageAnalyticsPage";
import AIPredictivePage from "./pages/AIPredictivePage";
import InsurancePortalPage from "./pages/InsurancePortalPage";
import SmartAlertsPage from "./pages/SmartAlertsPage";
import LogMessagesPage from "./pages/LogMessagesPage";
import LogGuidePage from "./pages/LogGuidePage";
import LogCreancesPage from "./pages/LogCreancesPage";
import LogActivityPage from "./pages/LogActivityPage";
import LogGPSTrackingPage from "./pages/LogGPSTrackingPage";
import LogPipelinePage from "./pages/LogPipelinePage";
import LogSubscriptionsPage from "./pages/LogSubscriptionsPage";
import LogTasksPage from "./pages/LogTasksPage";
import LogAdminFilesPage from "./pages/LogAdminFilesPage";
import LogPaymentTrackingPage from "./pages/LogPaymentTrackingPage";
import LandingPage from "./pages/LandingPage";
import PricingPage from "./pages/PricingPage";
import { InstitutionPage, CaseStudiesPage, BlogPage, BlogArticlePage, ContactPage } from "./pages/PublicPages";
import AdminVitrineAnalyticsPage from "./pages/AdminVitrineAnalyticsPage";
import StockForecastPage from "./pages/StockForecastPage";
import SubscriptionSelectPage from "./pages/SubscriptionSelectPage";
import InsurerRankingsPage from "./pages/InsurerRankingsPage";
import FeedbackPage from "./pages/FeedbackPage";
import LogSignalementPage from "./pages/LogSignalementPage";
import SecurityPage from "./pages/SecurityPage";
import PharmatechDashboard from "./pages/PharmatechDashboard";
import CopilotPage from "./pages/CopilotPage";
import PharmaChatPage from "./pages/PharmaChatPage";
import DigitalTwinPage from "./pages/DigitalTwinPage";
import PharmaSearchPage from "./pages/PharmaSearchPage";
import CopilotHistoryPage from "./pages/CopilotHistoryPage";
import PharmacovigilancePage from "./pages/PharmacovigilancePage";
import QualityScorePage from "./pages/QualityScorePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MyAccountPage from "./pages/MyAccountPage";
import { ChatbotWidget } from "./components/ChatbotWidget";
import CommandPalette from "./components/CommandPalette";

// Courier Pages
import CourierDashboardPage from "./pages/CourierDashboardPage";
import CourierCoursesPage from "./pages/CourierCoursesPage";
import CourierHistoryPage from "./pages/CourierHistoryPage";
import CourierNotificationsPage from "./pages/CourierNotificationsPage";

// Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor: queue failed write requests when offline
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response && error.config && ["post", "put", "patch", "delete"].includes((error.config.method || "").toLowerCase())) {
      // Network error on a write request → queue for later sync
      addToOfflineQueue({
        url: error.config.url,
        method: error.config.method?.toUpperCase(),
        headers: { "Content-Type": error.config.headers?.["Content-Type"] || "application/json", "Authorization": error.config.headers?.Authorization || "" },
        body: error.config.data,
      });
      toast.info("Action mise en file d'attente — sera synchronisée à la reconnexion.");
    }
    return Promise.reject(error);
  }
);

// Listen for online event to auto-sync
if (typeof window !== "undefined") {
  window.addEventListener("cmes-sync-complete", (e) => {
    const { synced, failed } = e.detail || {};
    if (synced > 0) toast.success(`${synced} action(s) synchronisée(s) avec succès.`);
    if (failed > 0) toast.warning(`${failed} action(s) n'ont pas pu être synchronisées.`);
  });
}

// Error Boundary - protège contre les erreurs DOM causées par les extensions navigateur
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    // Ignorer les erreurs removeChild causées par les extensions (Google Translate, etc.)
    if (error?.message?.includes("removeChild") || error?.message?.includes("insertBefore")) {
      this.setState({ hasError: false });
      return;
    }
    console.error("App error:", error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: "center", color: "#94A3B8" }}>
          <h2 style={{ color: "#fff", marginBottom: 10 }}>Une erreur est survenue</h2>
          <p>Veuillez rafraîchir la page (F5) ou désactiver les extensions de traduction.</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: "8px 24px", background: "#0EA5E9", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>
            Rafraîchir
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getToken);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          setStoredUser(response.data);
        } catch {
          clearSession();
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const data = response.data;
    // 2FA email OTP challenge
    if (data.status === "twofa_required") {
      return { twofaRequired: true, challengeId: data.challenge_id, emailHint: data.email_hint, message: data.message };
    }
    const { access_token, user: userData } = data;
    storeToken(access_token);
    setStoredUser(userData);
    setToken(access_token);
    setUser(userData);
    setTimeout(() => {
      toast.success("Mode en ligne — Connexion au serveur établie. Vous serez informé en cas de déconnexion.", { duration: 6000 });
    }, 2000);
    return userData;
  };

  const verifyOtp = async (challengeId, code) => {
    const response = await axios.post(`${API}/auth/login/verify-otp`, { challenge_id: challengeId, code });
    const { access_token, user: userData } = response.data;
    storeToken(access_token);
    setStoredUser(userData);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const data = response.data;
    
    if (data.status === "pending") {
      return { status: "pending", message: data.message };
    }
    
    const { access_token, user: newUser } = data;
    storeToken(access_token);
    setToken(access_token);
    setUser(newUser);
    return { status: "active", ...newUser };
  };

  const logout = useCallback(() => {
    clearSession();
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        setStoredUser(response.data);
        return response.data;
      } catch {}
    }
  }, [token]);

  // Session timeout: 10 minutes of inactivity
  useEffect(() => {
    if (!token) return;
    const TIMEOUT_MS = 10 * 60 * 1000;

    const updateActivity = () => setLastActivity();

    const checkTimeout = () => {
      const last = getLastActivity();
      if (last && Date.now() - last > TIMEOUT_MS) {
        logout();
        window.location.href = "/login?timeout=1";
      }
    };

    updateActivity();
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, updateActivity));
    const interval = setInterval(checkTimeout, 30000);

    return () => {
      events.forEach(e => window.removeEventListener(e, updateActivity));
      clearInterval(interval);
    };
  }, [token, logout]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, verifyOtp, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to plan selection if no subscription plan selected (pharmacy/etablissement role only)
  if ((user.role === "pharmacy" || user.role === "etablissement") && !user.subscription_plan && window.location.pathname !== "/choose-plan") {
    return <Navigate to="/choose-plan" replace />;
  }

  // Block access if subscription is pending payment (but NOT trial — trial users have access)
  if ((user.role === "pharmacy" || user.role === "etablissement") && user.subscription_plan && user.subscription_status && !["active", "trial"].includes(user.subscription_status) && window.location.pathname !== "/choose-plan") {
    return <Navigate to="/choose-plan" replace />;
  }

  // Courier users can only access /coursier/* routes
  if (user.role === "coursier" && (!allowedRoles || !allowedRoles.includes("coursier"))) {
    return <Navigate to="/coursier" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === "coursier") {
      return <Navigate to="/coursier" replace />;
    }
    return <Navigate to="/hub" replace />;
  }

  return children;
};

function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [apiDown, setApiDown] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const failCountRef = useRef(0);
  const wasOnlineRef = useRef(false);

  useEffect(() => {
    const on = () => { setIsOffline(false); setDismissed(false); };
    const off = () => { setIsOffline(true); setDismissed(false); setPendingCount(getOfflineQueue().length); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);

    const checkApi = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 6000);
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, {
          method: "GET",
          signal: controller.signal,
          cache: "no-store",
        });
        clearTimeout(timeout);
        if (res.ok) {
          wasOnlineRef.current = true;
          failCountRef.current = 0;
          setApiDown(false);
        } else {
          failCountRef.current += 1;
          if (wasOnlineRef.current && failCountRef.current >= 3) setApiDown(true);
        }
      } catch {
        clearTimeout(timeout);
        failCountRef.current += 1;
        if (wasOnlineRef.current && failCountRef.current >= 3) setApiDown(true);
      }
    };
    // Delay first check to let everything initialize
    const initialTimeout = setTimeout(checkApi, 5000);
    const interval = setInterval(checkApi, 15000);

    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  const showBanner = (isOffline || apiDown) && !dismissed;
  if (!showBanner) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-white text-center py-2.5 px-4 text-xs font-medium shadow-lg flex items-center justify-center gap-3 animate-in slide-in-from-top duration-300" data-testid="offline-banner">
      <span className="inline-flex items-center gap-2">
        <svg className="w-4 h-4 animate-pulse" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0" /></svg>
        {isOffline
          ? `Mode hors ligne \u2014 ${pendingCount > 0 ? `${pendingCount} action(s) en attente. ` : ""}Les donn\u00e9es en cache sont affich\u00e9es. Synchronisation automatique \u00e0 la reconnexion.`
          : "Connexion au serveur interrompue \u2014 V\u00e9rification en cours..."}
      </span>
      <button onClick={() => setDismissed(true)} className="ml-2 text-white/80 hover:text-white text-lg leading-none font-bold" data-testid="offline-banner-dismiss">&times;</button>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <OfflineBanner />
        <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)" }}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/v/:serviceId" element={<PublicVideoViewer />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/presentation" element={<PublicPresentationPage />} />
            <Route path="/tarifs" element={<PricingPage />} />
            <Route path="/institution" element={<InstitutionPage />} />
            <Route path="/cas-clients" element={<CaseStudiesPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/securite" element={<SecurityPage />} />
            <Route path="/choose-plan" element={
              <ProtectedRoute>
                <SubscriptionSelectPage />
              </ProtectedRoute>
            } />
            
            {/* Pharmacy Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <PharmacyDashboard />
              </ProtectedRoute>
            } />
            <Route path="/products" element={
              <ProtectedRoute>
                <ProductsPage />
              </ProtectedRoute>
            } />
            <Route path="/scanner" element={
              <ProtectedRoute>
                <ProductScannerPage />
              </ProtectedRoute>
            } />
            <Route path="/stock-intelligence" element={
              <ProtectedRoute>
                <StockIntelligencePage />
              </ProtectedRoute>
            } />
            <Route path="/import" element={
              <ProtectedRoute>
                <ImportCSV />
              </ProtectedRoute>
            } />
            <Route path="/destructions" element={
              <ProtectedRoute>
                <DestructionsPage />
              </ProtectedRoute>
            } />
            <Route path="/destruction-express" element={
              <ProtectedRoute>
                <DestructionExpress />
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <ReportsPage />
              </ProtectedRoute>
            } />
            <Route path="/notifications" element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/recommendations" element={
              <ProtectedRoute>
                <RecommendationsPage />
              </ProtectedRoute>
            } />
            <Route path="/sentinel" element={
              <ProtectedRoute>
                <SentinelPage />
              </ProtectedRoute>
            } />
            <Route path="/exchange" element={
              <ProtectedRoute>
                <PharmExchangePage />
              </ProtectedRoute>
            } />
            <Route path="/itpp" element={
              <ProtectedRoute>
                <ITPPPage />
              </ProtectedRoute>
            } />
            <Route path="/qrcode" element={
              <ProtectedRoute>
                <QRCodePage />
              </ProtectedRoute>
            } />
            <Route path="/monthly" element={
              <ProtectedRoute>
                <MonthlyDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/downloads" element={
              <ProtectedRoute allowedRoles={["superadmin"]}>
                <DownloadsPage />
              </ProtectedRoute>
            } />
            <Route path="/integration" element={
              <ProtectedRoute>
                <APIPage />
              </ProtectedRoute>
            } />
            <Route path="/subscription" element={
              <ProtectedRoute>
                <SubscriptionPage />
              </ProtectedRoute>
            } />
            <Route path="/deployment" element={
              <ProtectedRoute allowedRoles={["admin", "superadmin"]}>
                <DeploymentGuidePage />
              </ProtectedRoute>
            } />
            <Route path="/system-status" element={
              <ProtectedRoute allowedRoles={["admin", "superadmin"]}>
                <SystemStatusPage />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={["admin", "logistics", "superadmin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            
            {/* Super Admin Routes */}
            <Route path="/superadmin" element={
              <ProtectedRoute allowedRoles={["superadmin"]}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/subscriptions" element={
              <ProtectedRoute allowedRoles={["superadmin"]}>
                <SubscriptionManagementPage />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/analytics-vitrine" element={
              <ProtectedRoute allowedRoles={["superadmin"]}>
                <AdminVitrineAnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/training-videos" element={
              <ProtectedRoute allowedRoles={["superadmin"]}>
                <TrainingVideosPage />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/gestion" element={
              <ProtectedRoute allowedRoles={["superadmin", "directeur_financier", "agent_recouvrement", "agent_comptable", "consultation_interne"]}>
                <InternalFinancePage />
              </ProtectedRoute>
            } />
            
            {/* Hub / App Selector */}
            <Route path="/hub" element={
              <ProtectedRoute>
                <HubPage />
              </ProtectedRoute>
            } />

            {/* CMES-LOG Routes */}
            <Route path="/log" element={
              <ProtectedRoute>
                <LogDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/log/invoices" element={
              <ProtectedRoute>
                <LogInvoicesPage />
              </ProtectedRoute>
            } />
            <Route path="/log/payment-tracking" element={
              <ProtectedRoute>
                <LogPaymentTrackingPage />
              </ProtectedRoute>
            } />
            <Route path="/log/payments" element={<Navigate to="/log/creances" replace />} />
            <Route path="/log/insurers" element={
              <ProtectedRoute>
                <LogInsurersPage />
              </ProtectedRoute>
            } />
            <Route path="/log/bank-statements" element={
              <ProtectedRoute>
                <LogBankStatementsPage />
              </ProtectedRoute>
            } />
            <Route path="/log/deliveries" element={<Navigate to="/log/expeditions" replace />} />
            <Route path="/log/expeditions" element={
              <ProtectedRoute>
                <LogDeliveriesPage />
              </ProtectedRoute>
            } />
            <Route path="/log/analytics" element={
              <ProtectedRoute>
                <LogAnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/log/bordereaux" element={
              <ProtectedRoute>
                <LogBordereauxPage />
              </ProtectedRoute>
            } />
            <Route path="/log/courses" element={
              <ProtectedRoute>
                <LogCoursesPage />
              </ProtectedRoute>
            } />
            <Route path="/log/paiements" element={<Navigate to="/log/creances" replace />} />
            <Route path="/log/compliance" element={<Navigate to="/log/creances" replace />} />
            <Route path="/log/recoveries" element={<Navigate to="/log/creances" replace />} />
            <Route path="/log/benchmarking" element={<Navigate to="/log/analytics" replace />} />
            <Route path="/log/ia-dashboard" element={<Navigate to="/log/analytics" replace />} />
            <Route path="/log/reconciliation" element={<Navigate to="/log/bank-statements" replace />} />
            <Route path="/log/archives" element={
              <ProtectedRoute>
                <LogArchivesPage />
              </ProtectedRoute>
            } />
            <Route path="/log/documentation" element={
              <ProtectedRoute allowedRoles={["superadmin"]}>
                <LogDocumentationPage />
              </ProtectedRoute>
            } />
            <Route path="/log/guide" element={
              <ProtectedRoute>
                <LogGuidePage />
              </ProtectedRoute>
            } />
            <Route path="/log/mon-compte" element={
              <ProtectedRoute>
                <MyAccountPage />
              </ProtectedRoute>
            } />
            <Route path="/log/pos" element={
              <ProtectedRoute>
                <LogPOSPage />
              </ProtectedRoute>
            } />
            <Route path="/log/notifications" element={
              <ProtectedRoute>
                <LogNotificationsPage />
              </ProtectedRoute>
            } />
            <Route path="/log/rapport-mensuel" element={
              <ProtectedRoute>
                <LogMonthlyReportPage />
              </ProtectedRoute>
            } />
            <Route path="/log/tonnage" element={
              <ProtectedRoute>
                <TonnageAnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/pharma/tonnage" element={
              <ProtectedRoute>
                <TonnageAnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/log/ia-prédictive" element={
              <ProtectedRoute>
                <AIPredictivePage />
              </ProtectedRoute>
            } />
            <Route path="/pharma/ia-prédictive" element={
              <ProtectedRoute>
                <AIPredictivePage />
              </ProtectedRoute>
            } />
            <Route path="/pharma/stock-forecast" element={
              <ProtectedRoute>
                <StockForecastPage />
              </ProtectedRoute>
            } />
            <Route path="/log/insurer-rankings" element={
              <ProtectedRoute>
                <InsurerRankingsPage />
              </ProtectedRoute>
            } />
            <Route path="/feedback" element={
              <ProtectedRoute>
                <FeedbackPage />
              </ProtectedRoute>
            } />

            {/* Courier Routes */}
            <Route path="/coursier" element={
              <ProtectedRoute allowedRoles={["coursier"]}>
                <CourierDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/coursier/courses" element={
              <ProtectedRoute allowedRoles={["coursier"]}>
                <CourierCoursesPage />
              </ProtectedRoute>
            } />
            <Route path="/coursier/historique" element={
              <ProtectedRoute allowedRoles={["coursier"]}>
                <CourierHistoryPage />
              </ProtectedRoute>
            } />
            <Route path="/coursier/notifications" element={
              <ProtectedRoute allowedRoles={["coursier"]}>
                <CourierNotificationsPage />
              </ProtectedRoute>
            } />

            {/* Insurance Portal Routes */}
            <Route path="/assurance" element={
              <ProtectedRoute allowedRoles={["assurance", "superadmin"]}>
                <InsurancePortalPage />
              </ProtectedRoute>
            } />
            <Route path="/log/alertes" element={
              <ProtectedRoute>
                <SmartAlertsPage />
              </ProtectedRoute>
            } />
            <Route path="/log/messagerie" element={
              <ProtectedRoute>
                <LogMessagesPage />
              </ProtectedRoute>
            } />
            <Route path="/log/creances" element={
              <ProtectedRoute>
                <LogCreancesPage />
              </ProtectedRoute>
            } />
            <Route path="/log/historique" element={
              <ProtectedRoute>
                <LogActivityPage />
              </ProtectedRoute>
            } />
            <Route path="/log/gps" element={
              <ProtectedRoute allowedRoles={["superadmin", "admin", "pharmacy", "etablissement"]}>
                <LogGPSTrackingPage />
              </ProtectedRoute>
            } />
            <Route path="/log/pipeline" element={
              <ProtectedRoute>
                <LogPipelinePage />
              </ProtectedRoute>
            } />
            <Route path="/log/abonnements" element={
              <ProtectedRoute>
                <LogSubscriptionsPage />
              </ProtectedRoute>
            } />
            <Route path="/log/taches" element={
              <ProtectedRoute>
                <LogTasksPage />
              </ProtectedRoute>
            } />
            <Route path="/log/dossiers-pharmacies" element={
              <ProtectedRoute allowedRoles={["admin", "superadmin"]}>
                <LogAdminFilesPage />
              </ProtectedRoute>
            } />
            <Route path="/log/signalement" element={
              <ProtectedRoute>
                <LogSignalementPage />
              </ProtectedRoute>
            } />

            {/* CMES Pharmatech Routes */}
            <Route path="/pharmatech" element={
              <ProtectedRoute>
                <PharmatechDashboard />
              </ProtectedRoute>
            } />
            {/* Copilot fusionné avec Scanner Ordonnances (mai 2026) - redirection */}
            <Route path="/pharmatech/copilot" element={<Navigate to="/pharmatech/ordonnances" replace />} />
            <Route path="/pharmatech/chat" element={
              <ProtectedRoute>
                <PharmaChatPage />
              </ProtectedRoute>
            } />
            <Route path="/pharmatech/profil-patient" element={
              <ProtectedRoute>
                <DigitalTwinPage />
              </ProtectedRoute>
            } />
            <Route path="/pharmatech/digital-twin" element={
              <ProtectedRoute>
                <DigitalTwinPage />
              </ProtectedRoute>
            } />
            <Route path="/pharmatech/recherche" element={
              <ProtectedRoute>
                <PharmaSearchPage />
              </ProtectedRoute>
            } />
            <Route path="/pharmatech/historique" element={
              <ProtectedRoute>
                <CopilotHistoryPage />
              </ProtectedRoute>
            } />
            <Route path="/pharmatech/pharmacovigilance" element={
              <ProtectedRoute>
                <PharmacovigilancePage />
              </ProtectedRoute>
            } />
            <Route path="/pharmatech/score" element={
              <ProtectedRoute>
                <QualityScorePage />
              </ProtectedRoute>
            } />
            <Route path="/pharmatech/ordonnances" element={
              <ProtectedRoute>
                <PrescriptionScannerPage />
              </ProtectedRoute>
            } />
            <Route path="/pharmatech/fiches-conseil" element={
              <ProtectedRoute>
                <ConseilHistoryPage />
              </ProtectedRoute>
            } />

            {/* Public Routes (no auth needed - QR Code) */}
            <Route path="/urgence" element={<QRLandingPage />} />
            <Route path="/pharmacovigilance/:pharmacyCode" element={<PatientReportPage />} />
            <Route path="/déclaration-rapide" element={<QuickDeclarePage />} />
            
            {/* Default */}
            <Route path="/" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/hub" replace />} />
          </Routes>
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                color: '#334155'
              }
            }}
          />
          <ChatbotWrapper />
          <CommandPaletteWrapper />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

function ChatbotWrapper() {
  const { user } = useAuth();
  if (!user) return null;
  return <ChatbotWidget />;
}

function CommandPaletteWrapper() {
  const { user } = useAuth();
  if (!user) return null;
  return <CommandPalette />;
}

export default App;
